import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Slider, Button, Tooltip, Segmented, Collapse } from "antd";
import {
  RiPlayFill,
  RiPauseFill,
  RiReplay10Line,
  RiForward10Line,
} from "react-icons/ri";
import dayjs from "dayjs";
import { SPEEDS } from "../hooks/useTimeContext";
import { msFromTrackX } from "../utils/msFromTrackX";

const JUMP_MS = 10 * 1000;

// Width of the label gutter to the left of the shared time column. Every
// time-mapped row (alert dots, slider, coverage bars) starts exactly here,
// so one pixel-to-time scale serves the whole timeline.
const GUTTER_PX = 72;

// Reserved ruler lane at the top of the time area: the hover-time pill
// lives here, ABOVE the alert-dot lane, so it can never cover a dot (or
// anything else -- reserved space, same structural logic as the shared
// column itself). Roughly half the pill's width, for edge clamping.
const RULER_H_PX = 18;
const PILL_HALF_PX = 30;

const pctFromMs = (ms, range) => {
  const span = range.endMs - range.startMs;
  if (span <= 0) return 0;
  return ((ms - range.startMs) / span) * 100;
};

const msFromPct = (pct, range) =>
  range.startMs + (pct / 100) * (range.endMs - range.startMs);

// One CSS position for a time fraction inside the time column: the column
// begins after the gutter, so the same expression serves the playhead line,
// the hover line, and anything else pinned to an instant.
const columnLeft = (frac) =>
  `calc(${GUTTER_PX}px + (100% - ${GUTTER_PX}px) * ${Math.min(Math.max(frac, 0), 1)})`;

// The master scrubber. LAYOUT CONTRACT: everything that represents time
// lives in ONE shared column starting GUTTER_PX from the left edge of the
// container div (labels sit in the gutter). Alert dots, the slider track,
// and the per-person coverage bars therefore share a single pixel-to-time
// scale, and the playhead/hover lines are single full-height verticals that
// cross every row -- alignment is structural, not maintained by eye.
const ReplayTimeline = ({
  range,
  finePlayheadRef,
  subscribe,
  coarsePlayheadMs,
  isPlaying,
  speed,
  onPlay,
  onPause,
  onSpeedChange,
  onScrubChange,
  onScrubCommit,
  onJump,
  onCoverageSeek,
  onSubjectFocus,
  subjectId = null,
  alerts = [],
  onAlertMarkerClick,
  availability = [],
}) => {
  const [displayMs, setDisplayMs] = useState(finePlayheadRef.current);
  const [hover, setHover] = useState(null);
  const timeAreaRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribe((ms) => setDisplayMs(ms));
    return unsubscribe;
  }, [subscribe]);

  useEffect(() => {
    setDisplayMs(coarsePlayheadMs);
  }, [coarsePlayheadMs]);

  const pct = pctFromMs(displayMs, range);

  const alertMarkers = useMemo(
    () =>
      alerts
        .filter(
          (a) => a.timestamp >= range.startMs && a.timestamp <= range.endMs,
        )
        .map((a) => ({ ...a, pct: pctFromMs(a.timestamp, range) })),
    [alerts, range],
  );

  const handleTimeAreaMouseMove = (e) => {
    const area = timeAreaRef.current;
    if (!area) return;
    const rect = area.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    if (offsetX < GUTTER_PX) {
      setHover(null);
      return;
    }
    const ms = msFromTrackX(offsetX - GUTTER_PX, rect.width - GUTTER_PX, range);
    const pillX = Math.min(
      Math.max(offsetX, GUTTER_PX + PILL_HALF_PX),
      rect.width - PILL_HALF_PX,
    );
    setHover({ x: offsetX, pillX, ms });
  };

  const handleTimeAreaMouseLeave = () => setHover(null);

  // Clicking the NAME focuses the person without moving the clock; clicking
  // the BAR seeks to the clicked instant AND focuses (handleCoverageClick).
  // Two gestures, two meanings -- "show me this person" vs "take me to this
  // moment". The name span is a sibling of the bar, not a child, so these
  // never both fire and no stopPropagation is needed.
  const handleNameClick = (subject) => {
    if (!onSubjectFocus) return;
    onSubjectFocus(subject.personnelId);
  };

  const handleCoverageClick = (e, subject) => {
    if (!onCoverageSeek) return;
    // Bars span exactly the shared time column, so the bar's own rect and
    // the column agree; mapping through the bar keeps the handler local.
    const rect = e.currentTarget.getBoundingClientRect();
    const ms = msFromTrackX(e.clientX - rect.left, rect.width, range);
    onCoverageSeek(ms, subject.personnelId);
  };

  return (
    <Card size="small" bordered bodyStyle={{ padding: "12px 16px" }}>
      {/* Plain flex row, NOT antd Space: Space wraps each child in an
          ant-space-item span, so the readout's marginLeft:auto pushed
          against its own wrapper instead of the row and never reached
          the right edge. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          marginBottom: 4,
        }}
      >
        <Tooltip title="往前 10 秒">
          <Button
            size="small"
            type="text"
            icon={<RiReplay10Line />}
            onClick={() => onJump(-JUMP_MS)}
          />
        </Tooltip>
        <Tooltip title={isPlaying ? "暫停" : "播放"}>
          <Button
            size="small"
            type="text"
            icon={isPlaying ? <RiPauseFill /> : <RiPlayFill />}
            onClick={isPlaying ? onPause : onPlay}
          />
        </Tooltip>
        <Tooltip title="往後 10 秒">
          <Button
            size="small"
            type="text"
            icon={<RiForward10Line />}
            onClick={() => onJump(JUMP_MS)}
          />
        </Tooltip>

        <Segmented
          size="small"
          value={speed}
          onChange={onSpeedChange}
          options={SPEEDS.map((s) => ({ label: `${s}x`, value: s }))}
        />

        <span
          style={{
            marginLeft: "auto",
            fontVariantNumeric: "tabular-nums",
            fontSize: 15,
            fontWeight: 600,
            color: "#262626",
            fontFamily:
              "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
          }}
        >
          {dayjs(displayMs).format("YYYY-MM-DD HH:mm:ss")}
        </span>
      </div>

      <div
        ref={timeAreaRef}
        className="history-timeline"
        style={{ position: "relative", padding: "4px 0" }}
        onMouseMove={handleTimeAreaMouseMove}
        onMouseLeave={handleTimeAreaMouseLeave}
      >
        {/* Hover timestamp pill, in the ruler lane. */}
        {hover && (
          <>
            <div
              style={{
                position: "absolute",
                left: hover.pillX,
                top: 0,
                transform: "translateX(-50%)",
                backgroundColor: "rgba(0, 0, 0, 0.75)",
                color: "#fff",
                fontSize: 11,
                lineHeight: "16px",
                padding: "1px 6px",
                borderRadius: 4,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                zIndex: 3,
              }}
            >
              {dayjs(hover.ms).format("HH:mm:ss")}
            </div>
          </>
        )}

        {/* Ruler lane: reserved height for the hover pill (and any future
            tick labels), so the pill and the alert dots never share a row. */}
        <div style={{ height: RULER_H_PX }} />

        {/* Track region: its own relative context, so the playhead and
            hover lines (top:0 -> bottom:0 HERE) span exactly the dot lane
            through the coverage bars and stop below the ruler. */}
        <div style={{ position: "relative" }}>
          {/* Playhead: ONE vertical line through every time-mapped row. */}
          <div
            style={{
              position: "absolute",
              left: columnLeft(pct / 100),
              top: 0,
              bottom: 0,
              width: 2,
              transform: "translateX(-50%)",
              backgroundColor: "rgba(22, 119, 255, 0.45)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          {hover && (
            <div
              style={{
                position: "absolute",
                left: hover.x,
                top: 0,
                bottom: 0,
                width: 1,
                backgroundColor: "rgba(0, 0, 0, 0.25)",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
          )}

          {/* Alert-dot lane, inside the shared column. pointerEvents is
            re-enabled only on the dots so they never steal track drags. */}
          <div
            style={{
              position: "relative",
              height: 10,
              marginLeft: GUTTER_PX,
              pointerEvents: "none",
            }}
          >
            {alertMarkers.map((alert) => (
              <Tooltip
                key={alert.id}
                title={
                  <div>
                    <div>
                      {alert.personnelName}｜
                      {dayjs(alert.timestamp).format("HH:mm:ss")}
                    </div>
                    <div>{alert.message}</div>
                  </div>
                }
              >
                <div
                  onClick={() =>
                    onAlertMarkerClick && onAlertMarkerClick(alert)
                  }
                  style={{
                    position: "absolute",
                    left: `${alert.pct}%`,
                    top: 0,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      alert.severity === "critical" ? "#ff4d4f" : "#faad14",
                    transform: "translateX(-50%)",
                    cursor: "pointer",
                    pointerEvents: "auto",
                  }}
                />
              </Tooltip>
            ))}
          </div>

          <div style={{ marginLeft: GUTTER_PX }}>
            <Slider
              value={pct}
              tooltip={{ formatter: null }}
              onChange={(value) => {
                onScrubChange(msFromPct(value, range));
              }}
              onChangeComplete={(value) => {
                onScrubCommit(msFromPct(value, range));
              }}
              step={0.01}
            />
          </div>

          {availability.length > 0 && (
            <Collapse
              ghost
              size="small"
              expandIconPosition="start"
              style={{ marginTop: 4 }}
              items={[
                {
                  key: "availability",
                  label: (
                    <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                      人員資料涵蓋（{availability.length} 人）
                    </span>
                  ),
                  children: (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        width: "100%",
                      }}
                    >
                      {availability.map((subject) => {
                        const isFocused =
                          subjectId != null &&
                          subject.personnelId === subjectId;
                        return (
                          <div
                            key={subject.personnelId}
                            data-focused={isFocused ? "true" : undefined}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              // LAYOUT-NEUTRAL FOCUS STYLING ONLY. Every row here
                              // shares the timeline's single pixel-to-time scale
                              // with the slider, the alert dots and the playhead
                              // (see the LAYOUT CONTRACT above), and each bar
                              // starts exactly GUTTER_PX from the left edge. A
                              // border or horizontal padding on the focused row
                              // would shift THAT row's bar by its own width and
                              // silently break the alignment for one row only --
                              // so focus is carried by background and text
                              // colour, which change no box metrics. The vertical
                              // padding is applied to EVERY row, focused or not,
                              // so rows never jump as focus moves between them.
                              padding: "2px 0",
                              borderRadius: 3,
                              backgroundColor: isFocused
                                ? "rgba(22, 119, 255, 0.10)"
                                : "transparent",
                            }}
                          >
                            <span
                              onClick={() => handleNameClick(subject)}
                              title={subject.name}
                              style={{
                                fontSize: 11,
                                // Focused names take the playhead's blue, NOT the
                                // coverage bar's green: green already means "has
                                // data in this bucket" on the very next element,
                                // so reusing it for focus would make an empty row
                                // read as a full one.
                                color: isFocused ? "#1677ff" : "#8c8c8c",
                                fontWeight: isFocused ? 600 : 400,
                                width: GUTTER_PX,
                                paddingRight: 8,
                                boxSizing: "border-box",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                cursor: onSubjectFocus ? "pointer" : "default",
                              }}
                            >
                              {subject.name}
                            </span>
                            <div
                              onClick={(e) => handleCoverageClick(e, subject)}
                              style={{
                                flex: 1,
                                height: 6,
                                display: "flex",
                                borderRadius: 3,
                                overflow: "hidden",
                                backgroundColor: "#f0f0f0",
                                cursor: "pointer",
                              }}
                            >
                              {subject.buckets.split("").map((bit, i) => (
                                <div
                                  // eslint-disable-next-line react/no-array-index-key
                                  key={i}
                                  style={{
                                    flex: 1,
                                    backgroundColor:
                                      bit === "1" ? "#52c41a" : "transparent",
                                  }}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ),
                },
              ]}
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default ReplayTimeline;
